import { converters as tableConverters } from 'store/table';
import { IAdminSupplier, IEarningPeriod, IPartnerAdmin } from 'types/income';
import { loaders } from './admin-suppliers.loaders';
import { DEFAULT_EARNING_FEE_BPS } from './admin-suppliers.constants';
import {
  AdminSupplier,
  EarningPeriod,
  PartnerAdmin,
} from './admin-suppliers.types';

const convertSupplier = (supplier: IAdminSupplier): AdminSupplier => {
  return {
    id: supplier.id,
    name: supplier.name,
    systemName: supplier.systemName,
    logo: supplier.logoId,
    description: supplier.description ?? '',
    additionalInfo: supplier.additionalInfo ?? '',
    termsAndConditionsLink: supplier.termsAndConditionsLink ?? '',
    status: supplier.status,
    items: Number(supplier.activeItems ?? 0),
    orders: Number(supplier.completedOrders ?? 0),
    isParentCompany: supplier.isParentCompany ?? false,
    createdAt: supplier.creationDate,
    partnerPortalEnabled: Boolean(supplier.partnerPortalEnabled),
    earningFeeBps: Number(supplier.earningFeeBps ?? DEFAULT_EARNING_FEE_BPS),
  };
};

const convertSuppliers = (suppliers: IAdminSupplier[]): AdminSupplier[] => {
  return suppliers.map(convertSupplier);
};

const convertPartnerAdmin = (admin: IPartnerAdmin): PartnerAdmin => {
  return {
    id: admin.id,
    firstName: admin.firstName ?? '',
    lastName: admin.lastName ?? '',
    fullName: [admin.firstName, admin.lastName].filter(Boolean).join(' '),
    email: admin.email ?? '',
    phone: admin.phone ?? '',
    status: admin.status,
    root: Boolean(admin.root),
    createdAt: admin.creationDate,
    cognitoStatus: admin.cognitoStatus,
    cognito: admin.cognito
      ? {
          username: admin.cognito.username,
          sub: admin.cognito.sub,
          email: admin.cognito.email ?? null,
        }
      : admin.cognito,
  };
};

const convertPartnerAdmins = (admins: IPartnerAdmin[]): PartnerAdmin[] => {
  return admins.map(convertPartnerAdmin);
};

const convertEarningPeriod = (period: IEarningPeriod): EarningPeriod => {
  return {
    id: period.id,
    supplierId: period.supplierId,
    startsAt: period.startsAt,
    endsAt: period.endsAt ?? null,
    feeBps: Number(period.feeBps ?? DEFAULT_EARNING_FEE_BPS),
    createdAt: period.creationDate,
    isOpen: period.endsAt == null,
  };
};

const convertEarningPeriods = (periods: IEarningPeriod[]): EarningPeriod[] => {
  return periods.map(convertEarningPeriod);
};

export const converters = {
  getSupplier: ({ data }: Awaited<ReturnType<typeof loaders.getSupplier>>) => {
    return convertSupplier(data);
  },
  getSuppliers: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getSuppliers>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertSuppliers(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getPartnerAdmins: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getPartnerAdmins>>) => {
    return convertPartnerAdmins(data.data);
  },
  getPartnerAdmin: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getPartnerAdmin>>) => {
    return convertPartnerAdmin(data);
  },
  getEarningPeriods: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getEarningPeriods>>) => {
    return convertEarningPeriods(data);
  },
};
